import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FilterChip } from '@3dspinit/ui';
import { api, type FeedItem, type FeaturedChannel } from '../lib/api';
import { ChannelStrip } from '../components/ChannelStrip';
import { ObjectCard } from '../components/ObjectCard';

const CATEGORIES = [
  'Clothing',
  'Music',
  'Museums',
  'Home & Interiors',
  'Aerospace',
  'HVAC & Building Systems',
  'Entertainment',
  'Collectibles',
] as const;

type CreateTab = 'image' | 'text' | 'import';

export function DiscoveryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [channels, setChannels] = useState<FeaturedChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [createTab, setCreateTab] = useState<CreateTab>('text');
  const [draftPrompt, setDraftPrompt] = useState('');

  useEffect(() => {
    setLoading(true);
    void Promise.all([api.getFeed(), api.getFeatured()])
      .then(([feed, featured]) => {
        setItems(feed.items);
        setChannels(featured.channels);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load feed'))
      .finally(() => setLoading(false));
  }, []);

  const catalogItems = items;
  const filtered = useMemo(() => {
    if (!category) return catalogItems;
    return catalogItems.filter((item) =>
      `${item.title} ${item.channel?.name ?? ''}`.toLowerCase().includes(category.toLowerCase().split(' ')[0] ?? ''),
    );
  }, [catalogItems, category]);

  const emptyCategory = category && filtered.length === 0 && !loading;

  function goCreate() {
    if (createTab === 'import') {
      navigate('/workspace?tab=import');
      return;
    }
    if (createTab === 'image') {
      navigate('/workspace?tab=image');
      return;
    }
    const q = draftPrompt.trim() ? `?prompt=${encodeURIComponent(draftPrompt.trim())}` : '';
    navigate(`/workspace${q}`);
  }

  return (
    <main className="page">
      <section className="create-hero" aria-labelledby="create-hero-title">
        <p className="hero__eyebrow">3DSpin</p>
        <h1 id="create-hero-title" className="create-hero__title">
          What will you create in 3D?
        </h1>
        <p className="hero__body" style={{ marginBottom: '1rem' }}>
          Discover it. Spin it. Make it part of your world.
        </p>

        <div className="create-hero__tabs" role="tablist" aria-label="Create input">
          {(['image', 'text', 'import'] as CreateTab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={createTab === tab}
              className={createTab === tab ? 'active' : ''}
              onClick={() => setCreateTab(tab)}
            >
              {tab === 'image' ? 'Image' : tab === 'text' ? 'Text' : 'Import'}
            </button>
          ))}
        </div>

        <div className="create-hero__panel">
          {createTab === 'text' && (
            <label className="field" style={{ margin: 0 }}>
              <span className="sr-only">Prompt</span>
              <textarea
                className="prompt"
                style={{ minHeight: 88, margin: 0 }}
                placeholder="Describe a music-room prop, exhibition object, or collectible…"
                value={draftPrompt}
                onChange={(e) => setDraftPrompt(e.target.value)}
              />
            </label>
          )}
          {createTab === 'image' && (
            <div
              className="create-hero__drop"
              role="button"
              tabIndex={0}
              onClick={goCreate}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') goCreate();
              }}
            >
              Drop an image here or open the workspace
              <span className="muted" style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6 }}>
                Image-to-3D stays unavailable until Meshy credentials and contract tests pass.
              </span>
            </div>
          )}
          {createTab === 'import' && (
            <div className="create-hero__drop" role="button" tabIndex={0} onClick={goCreate} onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') goCreate();
            }}>
              Import GLB, glTF, OBJ, STL…
            </div>
          )}
          <div className="hero__actions" style={{ marginTop: '0.85rem' }}>
            <button type="button" className="btn primary" onClick={goCreate}>
              {createTab === 'text' ? 'Open Create workspace' : createTab === 'import' ? 'Import model' : 'Continue'}
            </button>
            <Link className="btn" to="/search">
              Browse objects
            </Link>
          </div>
        </div>
      </section>

      <section aria-labelledby="categories-title">
        <h2 id="categories-title" className="section-title">
          Browse by category
        </h2>
        <div className="category-row" role="group" aria-label="Categories">
          <FilterChip selected={!category} onClick={() => setCategory(null)}>
            All
          </FilterChip>
          {CATEGORIES.map((cat) => (
            <FilterChip key={cat} selected={category === cat} onClick={() => setCategory(cat)}>
              {cat}
            </FilterChip>
          ))}
        </div>
        {emptyCategory ? (
          <p className="muted">
            No pilot inventory in {category} yet.{' '}
            <Link to="/workspace">Create an object</Link>.
          </p>
        ) : null}
      </section>

      <ChannelStrip channels={channels} />

      <section aria-labelledby="catalog-title">
        <h2 id="catalog-title" className="section-title">
          Published objects
        </h2>
        <p className="muted" style={{ marginTop: '-0.5rem', marginBottom: '1rem', fontSize: '0.875rem' }}>
          Sample catalog models are labeled by creator — not your private drafts.
        </p>
        {loading && <p className="muted" role="status">Loading published objects…</p>}
        {error && <p className="err" role="alert">{error}</p>}
        {!loading && !error && items.length === 0 && (
          <p className="muted">Pilot catalog is empty. Seed locally or wait for reviewed assets.</p>
        )}
        <div className="masonry">
          {(category ? filtered : items).map((item) => (
            <ObjectCard key={item.id} item={item} />
          ))}
        </div>
      </section>
    </main>
  );
}
